import { useState, useCallback } from 'react';
import { Info } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/shared/components/ui/tabs';
import { useToast } from '@/shared/components/Toast';
import { useFPOverviewData } from '../../../hooks/useFPOverviewData';
import { useFPActionsData } from '../../../hooks/useFPActionsData';
import { useFPReferralsByServiceData } from '../../../hooks/useFPReferralsByServiceData';
import { useFPTopSearchesData } from '../../../hooks/useFPTopSearchesData';
import { useFPClientActionsData } from '../../../hooks/useFPClientActionsData';
import { useFPEngagementRateData } from '../../../hooks/useFPEngagementRateData';
import { useFPTopAdvisorsData } from '../../../hooks/useFPTopAdvisorsData';
import { useFPAdvisorRetentionData } from '../../../hooks/useFPAdvisorRetentionData';
import { useFPTrendingCategoriesData } from '../../../hooks/useFPTrendingCategoriesData';
import { useFPSearchVolumeData } from '../../../hooks/useFPSearchVolumeData';
import { useFPRegionalDistributionData } from '../../../hooks/useFPRegionalDistributionData';
import { useFPEmergingRegionsData } from '../../../hooks/useFPEmergingRegionsData';
import { useFPUnmetDemandData } from '../../../hooks/useFPUnmetDemandData';
import { useEngagementData } from '../../../hooks/useEngagementData';
import { useSearchData } from '../../../hooks/useSearchData';
import { useCSVExport } from '../../../hooks/useCSVExport';
import { ExportButton } from '../shared/ExportButton';
import {
  OverviewKpis,
  ReferralValueSection,
  ActionsSection,
  TrendSection,
  TopSearchesSection,
  ClientActionsSection,
  ClientSearchGeoSection,
  EngagementRateSection,
  ReferralsSection,
  TopAdvisorsSection,
  RetentionSection,
  TrendingSection,
  SearchVolumeSection,
} from '../financial-partners/FinancialPartnersSections';
import {
  RegionalDistributionSection,
  EmergingRegionsSection,
  UnmetDemandSection,
} from '../financial-partners/FinancialPartnersGeoSections';
import type { AnalyticsQueryParams } from '../../../types/analytics.types';

type FinancialPartnersTabProps = {
  queryParams: AnalyticsQueryParams;
  isActive?: boolean;
};

type SubTab = 'overview' | 'advisor-activity' | 'client-insights' | 'geographic';

export const FinancialPartnersTab = ({
  queryParams,
  isActive = true,
}: FinancialPartnersTabProps) => {
  const [subTab, setSubTab] = useState<SubTab>('overview');
  const { showToast } = useToast();

  const handleExportError = useCallback(
    (message: string) => showToast(`Export failed: ${message}. Please try again.`, 'error', 5000),
    [showToast],
  );

  // Only fetch tiles for the visible sub-tab so an idle Overview doesn't fire
  // every advisor/client query (and vice-versa). React Query caches, so a
  // tile is fetched the first time its sub-tab is opened, then reused.
  const onOverview = isActive && subTab === 'overview';
  const onAdvisor = isActive && subTab === 'advisor-activity';
  const onClient = isActive && subTab === 'client-insights';
  const onGeo = isActive && subTab === 'geographic';

  // KPI row appears on every sub-tab, so it stays enabled whenever the tab is.
  const overview = useFPOverviewData(queryParams, isActive);
  const actions = useFPActionsData(queryParams, onOverview || onAdvisor);
  const referrals = useFPReferralsByServiceData(queryParams, onOverview || onClient);
  const engagement = useEngagementData(queryParams, onOverview || onAdvisor);
  const topSearches = useFPTopSearchesData(queryParams, onAdvisor);
  const engagementRate = useFPEngagementRateData(queryParams, onAdvisor);
  const topAdvisors = useFPTopAdvisorsData(queryParams, onAdvisor);
  const retention = useFPAdvisorRetentionData(queryParams, onAdvisor);
  const clientActions = useFPClientActionsData(queryParams, onClient);
  const trending = useFPTrendingCategoriesData(queryParams, onClient);
  const searchVolume = useFPSearchVolumeData(queryParams, onClient);
  const regionalDist = useFPRegionalDistributionData(queryParams, onGeo);
  const emergingRegions = useFPEmergingRegionsData(queryParams, onGeo);
  const unmetDemand = useFPUnmetDemandData(queryParams, onGeo);
  // Client Searches by Geography reuses the existing search MV/component (spec:
  // dedupe & reuse). Fetch is scoped to CARE_SEEKER — clients can only search the
  // SP directory, so this is "demand for SPs by state". Aggregate-only (no firm scope).
  const search = useSearchData({ ...queryParams, userTypes: 'CARE_SEEKER' }, onGeo);
  const csvExport = useCSVExport({ onError: handleExportError });

  const newActionLabels = (actions.data?.actions ?? [])
    .filter((a) => a.isNew)
    .map((a) => a.label);

  const dateRange = queryParams.dateRange;
  const firmSelected = !!queryParams.partnerCompanyId;

  return (
    <div className="space-y-6 pt-6">
      {/* One Tabs root so Radix links each trigger to its own panel (aria). */}
      <Tabs value={subTab} onValueChange={(v) => setSubTab(v as SubTab)}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="advisor-activity">Advisor Activity</TabsTrigger>
            <TabsTrigger value="client-insights">Client Insights</TabsTrigger>
            <TabsTrigger value="geographic">Geographic</TabsTrigger>
          </TabsList>
          <ExportButton
            onClick={() =>
              csvExport.exportCSV(
                'financial-partners',
                // Cast to the exportCSV param shape, which keeps excludeInternal
                // a boolean instead of pretending every value is a string.
                queryParams as Record<string, string | number | boolean | undefined>,
              )
            }
            isLoading={csvExport.isExporting}
          />
        </div>

        {newActionLabels.length > 0 && (
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
            <span>
              Metrics reflect the last hourly materialized-view refresh. Newly-instrumented advisor
              actions — {newActionLabels.join(', ')} — show limited history and fill in going
              forward.
            </span>
          </div>
        )}

        <TabsContent value="overview">
          <div className="space-y-6">
            <OverviewKpis overview={overview} dateRange={dateRange} />
            <ReferralValueSection overview={overview} />
            <ActionsSection actions={actions} firmSelected={firmSelected} />
            <TrendSection engagement={engagement} />
            <ReferralsSection referrals={referrals} />
          </div>
        </TabsContent>

        <TabsContent value="advisor-activity">
          <div className="space-y-6">
            <OverviewKpis overview={overview} dateRange={dateRange} />
            <ActionsSection actions={actions} firmSelected={firmSelected} />
            <TopAdvisorsSection topAdvisors={topAdvisors} />
            <EngagementRateSection engagementRate={engagementRate} />
            <RetentionSection retention={retention} />
            <TopSearchesSection topSearches={topSearches} />
            <TrendSection engagement={engagement} />
          </div>
        </TabsContent>

        <TabsContent value="client-insights">
          <div className="space-y-6">
            <OverviewKpis overview={overview} dateRange={dateRange} />
            <ClientActionsSection clientActions={clientActions} />
            <TrendingSection trending={trending} />
            <SearchVolumeSection searchVolume={searchVolume} />
            <ReferralsSection referrals={referrals} />
          </div>
        </TabsContent>

        <TabsContent value="geographic">
          <div className="space-y-6">
            <OverviewKpis overview={overview} dateRange={dateRange} />
            <ClientSearchGeoSection search={search} />
            <RegionalDistributionSection regional={regionalDist} />
            <EmergingRegionsSection emerging={emergingRegions} />
            <UnmetDemandSection unmetDemand={unmetDemand} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
